import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'

/**
 * Warnung vor dem Verlassen einer Seite mit ungespeicherten Eingaben.
 *
 * Zwei Wege führen von einer Seite weg, und sie brauchen verschiedene Mittel:
 *
 * * **Innerhalb der Anwendung** — ein Klick in der Navigation. Hier greift der
 *   Router (`onBeforeRouteLeave`), und wir können einen eigenen Dialog zeigen.
 * * **Aus dem Browser heraus** — Schliessen des Reiters, Neuladen, Zurück. Hier
 *   lässt der Browser keinen eigenen Dialog zu; nur `beforeunload` mit dem
 *   browsereigenen Text ist möglich. Der Wortlaut ist nicht beeinflussbar.
 *
 * Der Zustand gilt als geändert, wenn die serialisierte Form von der zuletzt
 * gemerkten abweicht. Gemerkt wird beim Aufbau der Seite, nach dem Laden der
 * Daten und nach jedem erfolgreichen Speichern — deshalb schlägt das Speichern
 * selbst keine Warnung los, obwohl es die Seite verlässt.
 */

type Aufloesung = (verlassen: boolean) => void

/** Zustand des Dialogs; `UnsavedChangesDialog.vue` hängt daran. */
export const verlassenDialog = reactive({
  offen: false,
  aufloesen: null as Aufloesung | null,
})

/** Öffnet den Dialog und wartet auf die Entscheidung. */
export function fragenObVerlassen(): Promise<boolean> {
  // Ein zweiter Aufruf bei offenem Dialog würde die erste Zusage überschreiben
  // und die Navigation hängen lassen.
  if (verlassenDialog.offen) return Promise.resolve(false)

  return new Promise<boolean>((resolve) => {
    verlassenDialog.offen = true
    verlassenDialog.aufloesen = resolve
  })
}

/** Antwort aus dem Dialog. */
export function antwortVerlassen(verlassen: boolean): void {
  const aufloesen = verlassenDialog.aufloesen
  verlassenDialog.offen = false
  verlassenDialog.aufloesen = null
  aufloesen?.(verlassen)
}

/**
 * Meldet ungespeicherte Eingaben an, solange die Seite angezeigt wird.
 *
 * @param stand Liefert den Zustand, der gespeichert werden soll. Alles, was
 *   `JSON.stringify` abbilden kann; üblicherweise genau die Nutzlast, die beim
 *   Speichern an den Server geht.
 */
export function useUnsavedChanges(stand: () => unknown) {
  const gemerkt = ref<string | null>(null)

  function serialisiere(): string {
    try {
      return JSON.stringify(stand() ?? null)
    } catch {
      // Ein Zirkelbezug darf die Seite nicht lahmlegen. Lieber keine Warnung
      // als ein Absturz beim Blättern.
      return ''
    }
  }

  /** Den aktuellen Zustand als „gespeichert" merken. */
  function merkeStand(): void {
    gemerkt.value = serialisiere()
  }

  function istGeaendert(): boolean {
    return gemerkt.value !== null && gemerkt.value !== serialisiere()
  }

  function beimEntladen(ereignis: BeforeUnloadEvent): void {
    if (!istGeaendert()) return
    ereignis.preventDefault()
    // Ältere Browser werten nur `returnValue` aus.
    ereignis.returnValue = ''
  }

  onMounted(() => {
    // Beim Aufbau merken, damit auch ein **neuer** Datensatz zählt: dort wird
    // nichts geladen, der leere Stand ist der Vergleichspunkt.
    merkeStand()
    window.addEventListener('beforeunload', beimEntladen)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', beimEntladen)
  })

  onBeforeRouteLeave(async () => {
    if (!istGeaendert()) return true
    return await fragenObVerlassen()
  })

  return { merkeStand, istGeaendert }
}
